import { useTranslation } from 'react-i18next';
import {
  Modal,
  Button,
  Checkbox,
  Text,
  Select,
  FormRow,
  Input
} from '@efast_public/fjd-component-library';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
  Payment,
  PaymentServiceResponse,
  PaymentServices
} from '../api/govos.types';
import { GovosApiRoutes } from '../api/govos-api';
import { PaymentModalRouter } from './payment/PaymentModalRouter';
import { useTemplateNameValidation } from '../hooks/useTemplateNameValidation';

interface EditPaymentConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  startAppId: string | number;
  processId?: string | number;
  templateId?: string | number;
  payment?: Payment;
  isTemplatePage?: boolean;
  templateName?: string;
  existingNames?: string[];
}

export function EditPaymentConfigModal({
  isOpen,
  onClose,
  startAppId,
  processId,
  templateId,
  payment,
  isTemplatePage,
  templateName,
  existingNames
}: EditPaymentConfigModalProps) {
  const { t } = useTranslation();

  const [primaryButtonClickCount, setPrimaryButtonClickCount] = useState(0);
  const [selectedService, setSelectedService] = useState<
    PaymentServices | undefined
  >();
  const [shouldSaveAsTemplate, setShouldSaveAsTemplate] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [templateNameInModal, setTemplateNameInModal] = useState(
    templateName ?? ''
  );

  const isTemplateMode = templateId != null;

  const { templateNameError, validateTemplateName, clearTemplateNameError } =
    useTemplateNameValidation({
      currentTemplateName: isTemplatePage ? templateName : undefined,
      existingNames,
      t,
      validationKeyPrefix: 'EditPaymentConfigModal'
    });

  const { data: paymentServices } = useQuery<PaymentServiceResponse>({
    queryKey: [GovosApiRoutes.paymentServices(startAppId)]
  });

  useEffect(() => {
    if (paymentServices) {
      setSelectedService(
        paymentServices.paymentServices.find(
          (service) => service.serviceId === payment?.paymentService?.id
        )
      );
    }
  }, [payment?.paymentService?.id, paymentServices]);

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Modal.Header id="modal-header">
        <Text size="xl" strong>
          {t('EditPaymentConfigModal.title')}
        </Text>
      </Modal.Header>
      <Modal.Content>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Payment Service Selection */}
          <FormRow label={t('EditPaymentConfigModal.fields.paymentService')}>
            <Select
              onChange={(value) => {
                const selectedValue = value.at(0)?.value;
                setSelectedService(
                  paymentServices?.paymentServices.find(
                    (service) => String(service.serviceId) === selectedValue
                  )
                );
              }}
              options={
                paymentServices?.paymentServices.map((service) => ({
                  value: String(service.serviceId),
                  label: service.title
                })) || []
              }
              defaultOptions={
                paymentServices && (payment?.paymentService?.id ?? 0) > -1
                  ? paymentServices.paymentServices
                      .filter(
                        (zs) => zs.serviceId === payment?.paymentService?.id
                      )
                      .map((service) => ({
                        value: String(service.serviceId),
                        label: service.title
                      }))
                  : []
              }
              placeholder={
                paymentServices?.paymentServices?.length === 0
                  ? t('select.noOptionsAvailable')
                  : t('select.placeholder')
              }
              disabled={paymentServices?.paymentServices?.length === 0}
              style={{ width: '100%' }}
            />
          </FormRow>

          {isTemplatePage && isTemplateMode && (
            <FormRow
              label={t('EditPaymentConfigModal.templateName.label')}
              validationMessage={templateNameError}
              variant={templateNameError ? 'error' : undefined}
            >
              <Input
                value={templateNameInModal}
                onChange={(e) => {
                  setTemplateNameInModal(e.target.value);
                  clearTemplateNameError();
                }}
                placeholder={t(
                  'EditPaymentConfigModal.templateName.placeholder'
                )}
                style={{ width: '100%' }}
              />
            </FormRow>
          )}

          {!isTemplatePage && !isTemplateMode && (
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
            >
              <Checkbox
                checked={shouldSaveAsTemplate}
                onChange={(e) =>
                  setShouldSaveAsTemplate(e.currentTarget.checked)
                }
                label={t('EditPaymentConfigModal.saveAsTemplate.checkbox')}
              />
              {shouldSaveAsTemplate && (
                <FormRow
                  label={t('EditPaymentConfigModal.saveAsTemplate.nameLabel')}
                  validationMessage={templateNameError}
                  variant={templateNameError ? 'error' : undefined}
                >
                  <Input
                    value={newTemplateName}
                    onChange={(e) => {
                      setNewTemplateName(e.target.value);
                      clearTemplateNameError();
                    }}
                    placeholder={t(
                      'EditPaymentConfigModal.saveAsTemplate.namePlaceholder'
                    )}
                    style={{ width: '100%' }}
                  />
                </FormRow>
              )}
            </div>
          )}

          {/* Payment Service Specific Form */}
          {selectedService && paymentServices && (
            <PaymentModalRouter
              parentState={{ selectedService }}
              paymentServices={paymentServices.paymentServices}
              startAppId={startAppId}
              processId={processId}
              templateId={templateId}
              payment={payment}
              isParentFormValid={true}
              onSuccess={onClose}
              primaryButtonClickCount={primaryButtonClickCount}
              setPrimaryButtonClickCount={setPrimaryButtonClickCount}
              shouldSaveAsTemplate={shouldSaveAsTemplate}
              newTemplateName={newTemplateName}
            />
          )}
        </div>
      </Modal.Content>
      <Modal.Footer>
        <div style={{ display: 'flex', gap: '12px', width: '100%' }}>
          <Button
            variant="secondary-outline"
            onClick={onClose}
            style={{ flex: 1 }}
          >
            {t('EditPaymentConfigModal.actions.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              if (shouldSaveAsTemplate && !isTemplateMode) {
                if (!validateTemplateName(newTemplateName)) {
                  return;
                }
              }
              if (isTemplatePage && isTemplateMode) {
                if (!validateTemplateName(templateNameInModal)) {
                  return;
                }
              }
              setPrimaryButtonClickCount(primaryButtonClickCount + 1);
            }}
            style={{ flex: 1 }}
          >
            {t('EditPaymentConfigModal.actions.save')}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
